import { alignForward, FreeList } from './FreeList';

const DEFAULT_ALIGNMENT = 1;

let nextGPUBufferId = 0;

/**
 * Module-scoped array of all BufferManager instances created during runtime.
 * @type {WeakRef<BufferManager>[]}
 */
const sBufferManagers = [];

/**
 * Manages a pool of GPU buffers, automatically creating new buffers as needed.
 * Provides efficient allocation and deallocation of memory blocks within these buffers.
 */
export class BufferManager {
  /**
   * Default size of each buffer entry in bytes.
   * This is the size of each individual allocation that can be made from the buffers.
   * It is set during the BufferManager construction, but can vary based on the requested size.
   * @type {number}
   */
  #defaultBufferEntrySize = 0;

  /**
   * List of managed free lists.
   * @type {FreeList[]}
   */
  #freeLists = [];

  /**
   * @type {boolean} Whether frees are batched or happen immediately.
   * Batching frees can help prevent reusing the same portion of a buffer
   * multiple times in one frame, which can cause performance stalls.
   */
  #freesAreBatched = false;

  /**
   * @type {boolean} Whether to destroy empty buffers when they are no longer needed.
   * If false, empty buffers will be kept in the free lists for future allocations.
   * If true, empty buffers will be destroyed immediately when they become empty.
   */
  #destroyEmptyBuffers = false;

  /**
   * List of blocks to free.
   * @type {MemoryBlock[]}
   */
  #batchedBlocks = [];

  /**
   * Callback to create new GPU buffers.
   * @param {size: number} [size] - Size of the buffer to create in bytes.
   * @type {(size: number) => WebGLBuffer|GPUBuffer}
   */
  #createBufferCallback = (size) => {};

  /**
   * Callback to destroy GPU buffers.
   * @type {(buffer: WebGLBuffer|GPUBuffer) => void}
   */
  #destroyBufferCallback = () => {};

  /**
   * Creates a new BufferManager instance.
   *
   * @param {number} defaultSize - Default size of each buffer entry in bytes
   * @param {(size: number) => WebGLBuffer|GPUBuffer} createBufferCallback - Function to create new GPU buffers
   * @param {(buffer: WebGLBuffer|GPUBuffer) => void} destroyBufferCallback - Function to destroy a GPU buffer
   * @param {Object} [options] - Options controlling the manager behavior
   * @param {boolean} [options.freesAreBatched = false] - Indicates whether free operations are executed batched or individually
   * @param {boolean} [options.destroyEmptyBuffers = true] - Indicates whether empty buffers are kept or destroyed
   */
  constructor(defaultSize, createBufferCallback, destroyBufferCallback, options = {}) {
    this.#defaultBufferEntrySize = defaultSize;
    this.#createBufferCallback = createBufferCallback;
    this.#destroyBufferCallback = destroyBufferCallback;
    this.#freesAreBatched = 'freesAreBatched' in options ? options.freesAreBatched : false;
    this.#destroyEmptyBuffers = 'destroyEmptyBuffers' in options ? options.destroyEmptyBuffers : true;

    // Register this buffer manager to support memory statistics
    if (typeof WeakRef !== 'undefined') {
      sBufferManagers.push(new WeakRef(this));
    }
  }

  /**
   * Destroys all buffers and clears the free lists.
   * This should be called when the BufferManager is no longer needed.
   */
  dtor() {
    // Flush any batched free blocks before destroying buffers
    this.flushFreeBlocks();

    for (const freeList of this.#freeLists) {
      const buffer = freeList.getBuffer();
      this.#destroyBufferCallback(buffer);
      freeList.clear();
    }

    this.#createBufferCallback = null;
    this.#destroyBufferCallback = null;

    if (typeof WeakRef !== 'undefined') {
      const index = sBufferManagers.findIndex((ref) => ref.deref() === this);
      if (index !== -1) {
        sBufferManagers.splice(index, 1);
      }
    }
  }

  /**
   * Allocates a block of memory with the specified size and alignment.
   * Creates a new buffer if necessary.
   *
   * @param {number} size - Size of the memory block to allocate in bytes
   * @param {number} [alignment=DEFAULT_ALIGNMENT] - Alignment requirement in bytes
   * @returns {MemoryBlock} Allocated memory block
   */
  allocate(size, alignment = DEFAULT_ALIGNMENT) {
    let index = this.#findBufferEntry(size, alignment);
    if (index === null) {
      // Ensure the buffer size is at least the default entry size. For some cases, the
      // requested size may be larger than the default entry size, so we use the maximum.
      let bufferSize = Math.max(alignForward(size, 4), this.#defaultBufferEntrySize);

      const gpuBuffer = this.#createBufferCallback(bufferSize);
      gpuBuffer.id = nextGPUBufferId++;

      const freeList = new FreeList(bufferSize, gpuBuffer);
      this.#freeLists.push(freeList);
      index = this.#freeLists.length - 1;
    }
    const entry = this.#freeLists[index];
    const block = entry.allocate(size, alignment);
    return block;
  }

  /**
   * Either frees the block immediately or adds it to the batched list.
   *
   * @param {MemoryBlock} block - The memory block to free
   */
  free(block) {
    if (!block || block.isFree) {
      return;
    }

    if (this.#freesAreBatched) {
      this.#batchedBlocks.push(block);
    } else {
      this.#freeBlock(block);
    }
  }

  /**
   * Flushes all batched free blocks.
   */
  flushFreeBlocks() {
    if (!this.#freesAreBatched) {
      return;
    }

    for (const block of this.#batchedBlocks) {
      this.#freeBlock(block);
    }
    this.#batchedBlocks.length = 0;
  }

  /**
   * Remove all buffers from the cache (since those were all deallocated when the context was lost)
   */
  resetAfterContextLoss() {
    for (const freeList of this.#freeLists) {
      freeList.clear();
    }
    this.#freeLists.length = 0;
    this.#batchedBlocks.length = 0;
    nextGPUBufferId = 0;
  }

  /**
   * Exposes the internal FreeList array for use in debugging.
   *
   * This ought to be revisited later. The FreeLists should best not be exposed or at least more obfuscated
   * so that internal structure is not exposed via the public API.
   *
   * @returns {FreeList[]} The internal array of FreeLists.
   */
  getFreeLists() {
    return this.#freeLists;
  }

  /**
   * Gets the total size of all allocated buffers.
   *
   * @returns {number} Total size in bytes
   */
  getTotalSize() {
    let totalSize = 0;
    for (const freeList of this.#freeLists) {
      totalSize += freeList.getCapacity();
    }
    return totalSize;
  }

  /**
   * Gets the size of a single buffer entry.
   *
   * @returns {number} Buffer size in bytes
   */
  getDefaultBufferSize() {
    return this.#defaultBufferEntrySize;
  }

  /**
   * Gets the total amount of free space across all buffers.
   *
   * @returns {number} Total free space in bytes
   */
  getTotalFreeSpace() {
    let freeSpace = 0;
    for (const freeList of this.#freeLists) {
      freeSpace += freeList.getFreeSpace();
    }
    return freeSpace;
  }

  /**
   * Gets the total amount of used space across all buffers.
   *
   * @returns {number} Total used space in bytes
   */
  getUsedBytes() {
    let freeSpace = this.getTotalFreeSpace();
    let totalSize = this.getTotalSize();
    return totalSize - freeSpace;
  }

  /**
   * Finds a buffer entry that can accommodate the requested allocation.
   * Searches from newest to oldest buffer.
   *
   * @private
   * @param {number} size - Required size in bytes
   * @param {number} [alignment=DEFAULT_ALIGNMENT] - Alignment requirement in bytes
   * @returns {number|null} Index of suitable buffer entry, or null if none found
   */
  #findBufferEntry(size, alignment = DEFAULT_ALIGNMENT) {
    for (let i = this.#freeLists.length - 1; i >= 0; --i) {
      const entry = this.#freeLists[i];
      if (entry.canAllocate(size, alignment)) {
        return i;
      }
    }
    return null;
  }

  /**
   * Frees a previously allocated memory block.
   * If the buffer becomes completely empty, it is destroyed if configured to do so.
   *
   * @private
   * @param {MemoryBlock} block - The memory block to free
   */
  #freeBlock(block) {
    const freeList = block.freeList;
    freeList.free(block);

    if (this.#destroyEmptyBuffers) {
      if (freeList.getFreeSpace() === freeList.getCapacity()) {
        // If the free list is completely empty, remove it
        const index = this.#freeLists.indexOf(freeList);
        const buffer = freeList.getBuffer();
        this.#destroyBufferCallback(buffer);
        if (index !== -1) {
          this.#freeLists.splice(index, 1);
        }
      }
    }
  }

  /**
   * Gets total memory statistics across all BufferManagers.
   *
   * @returns {{totalMemory: number, totalAllocatedMemory: number}} Total memory statistics
   */
  static getTotalMemoryStatistics() {
    let totalMemory = 0;
    let totalAllocatedMemory = 0;

    for (let i = 0; i < sBufferManagers.length; i++) {
      const bufferManager = sBufferManagers[i].deref();
      if (bufferManager) {
        totalMemory += bufferManager.getTotalSize();
        totalAllocatedMemory += bufferManager.getUsedBytes();
      }
    }

    return {
      totalMemory,
      totalAllocatedMemory
    };
  }

  /**
   * Returns weak references to all BufferManagers used by LMV.
   *
   * @returns { WeakRef<BufferManager>[] } Array of weakly-referenced BufferManagers.
   */
  static getInstances() {
    return sBufferManagers;
  }
}